import { useEffect, useRef, useState } from 'react';
import { useViewModel } from "mvvm/View";
import { QuestionViewModel } from "../ViewModels/QuestionViewModel";
import TextArea from "antd/es/input/TextArea";
import { ArrowUpOutlined, MailOutlined } from '@ant-design/icons';
import { useAppSelector, useDarkMode, useThemeColors } from "redux/hooks";
import { ConditionalTooltip } from './ConditionalTooltip';

export function QuestionView(props: { model: QuestionViewModel; hasSelectedJob: boolean; centered?: boolean; isAnimating?: boolean }) {
    const model = useViewModel(props.model);
    const { isAnimating = false } = props;
    const suggestRequests = useAppSelector(state => state.user.settings?.suggest_requests ?? true);
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const showEmailToggle = useAppSelector(state => state.user.settings?.show_email_toggle ?? false);
    const allowEmail = useAppSelector(state => state.user.settings?.allow_email ?? false);
    const agentMemory = useAppSelector(state => state.user.settings?.agent_memory ?? "");
    const userID = useAppSelector(state => state.user.userID);
    const users = useAppSelector(state => state.user.users);
    const userEmail = users.find(u => u.userID === userID)?.email;
    const [sendEmailResponse, setSendEmailResponse] = useState(false);

    const textAreaRef = useRef<any>(null);
    const [, setRows] = useState(1);
    const [placeholder, setPlaceholder] = useState("");
    const [shuffledQuestions, setShuffledQuestions] = useState<string[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isPlaceholderTransitioning, setIsPlaceholderTransitioning] = useState(false);

    useEffect(() => {
            const textArea = textAreaRef.current?.resizableTextArea?.textArea;
            if (textArea) {
            // Calculate number of lines
            const lineHeight = parseInt(window.getComputedStyle(textArea).lineHeight);
            const lines = Math.ceil(textArea.scrollHeight / lineHeight);
            setRows(Math.min(lines, 10)); // Cap at 10 rows
        }
    }, [model.inputText]);

    // Force resize recalculation when placeholder changes
    useEffect(() => {
        // Only resize if textarea is empty (placeholder is visible)
        if (model.inputText) return;

        const textArea = textAreaRef.current?.resizableTextArea?.textArea;
        if (!textArea) return;

        // Create a hidden element to measure placeholder text
        const measureEl = document.createElement('div');
        const computedStyle = window.getComputedStyle(textArea);
        measureEl.style.position = 'absolute';
        measureEl.style.visibility = 'hidden';
        measureEl.style.whiteSpace = 'pre-wrap';
        measureEl.style.wordWrap = 'break-word';
        measureEl.style.width = computedStyle.width;
        measureEl.style.fontSize = computedStyle.fontSize;
        measureEl.style.fontFamily = computedStyle.fontFamily;
        measureEl.style.lineHeight = computedStyle.lineHeight;
        measureEl.style.padding = computedStyle.padding;
        measureEl.style.border = computedStyle.border;
        measureEl.style.boxSizing = computedStyle.boxSizing;
        measureEl.textContent = placeholder || '';

        document.body.appendChild(measureEl);
        const height = measureEl.offsetHeight;
        document.body.removeChild(measureEl);

        // Set the textarea height based on measured placeholder
        const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.3;
        const minHeight = lineHeight;
        const calculatedHeight = Math.max(minHeight, height);
        const maxHeight = lineHeight * 10; // maxRows: 10

        // Trigger resize by calling the resizableTextArea's internal resize
        setTimeout(() => {
            const resizableTextArea = textAreaRef.current?.resizableTextArea;
            if (resizableTextArea && typeof resizableTextArea.resizeTextArea === 'function') {
                resizableTextArea.resizeTextArea();
            } else {
                // Fallback: manually set height
                textArea.style.height = 'auto';
                const finalHeight = Math.min(calculatedHeight, maxHeight);
                textArea.style.height = `${finalHeight}px`;
            }
        }, 0);
    }, [placeholder, model.inputText]);

    const placeholderSourceJob = model.replyTargetJob || model.page.selectedJob;
    const placeholderConversationKey = placeholderSourceJob &&
        placeholderSourceJob.outputState.status === "loaded"
        ? JSON.stringify(placeholderSourceJob.outputState.output.Conversation?.content ?? null)
        : null;

    // Initialize and shuffle questions
    useEffect(() => {
        // If suggest_requests is false, always use static placeholder
        if (!suggestRequests) {
            setPlaceholder(model.page.jobService.model.shortDesc);
            setShuffledQuestions([]);
            return;
        }

        const isCentered = props.centered || false;

        // Check if we have a selected job with a follow-up question
        const followUpJob = placeholderSourceJob;
        if (followUpJob &&
            followUpJob.status !== "RUNNING" &&
            followUpJob.outputState.status === "loaded" &&
            followUpJob.outputState.output.Conversation &&
            typeof followUpJob.outputState.output.Conversation.content === 'object' &&
            followUpJob.outputState.output.Conversation.content.follow_up_question) {

            const followUpQuestion = followUpJob.outputState.output.Conversation.content.follow_up_question;

            // Check if follow_up_question is an array
            if (Array.isArray(followUpQuestion) && followUpQuestion.length > 0) {
                // Shuffle the follow-up questions and set up for cycling
                const shuffled = [...followUpQuestion];
                for (let i = shuffled.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                }
                setShuffledQuestions(shuffled);
                setCurrentQuestionIndex(0);
                setPlaceholder(shuffled[0]);
            } else if (typeof followUpQuestion === 'string') {
                // Handle legacy single string format
                setPlaceholder(followUpQuestion);
                setShuffledQuestions([]);
            } else {
                setPlaceholder(model.page.jobService.model.shortDesc);
                setShuffledQuestions([]);
            }
            return;
        }

        if (isCentered && model.page.jobService.model.longDesc) {
            const questions = model.page.jobService.model.longDesc.split('\\n').filter(q => q.trim());
            if (questions.length > 0) {
                // Fisher-Yates shuffle
                const shuffled = [...questions];
                for (let i = shuffled.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                }
                setShuffledQuestions(shuffled);
                setCurrentQuestionIndex(0);
                setPlaceholder(shuffled[0]);
            } else {
                setPlaceholder(model.page.jobService.model.shortDesc);
            }
        } else {
            setPlaceholder(model.page.jobService.model.shortDesc);
            setShuffledQuestions([]);
        }
    }, [
        suggestRequests,
        props.centered,
        model.page.jobService.model.longDesc,
        model.page.jobService.model.shortDesc,
        placeholderSourceJob,
        placeholderConversationKey
    ]);

    // Cycle through questions every 20 seconds (only when input is empty)
    useEffect(() => {
        if (!suggestRequests) return; // Disable cycling if suggest_requests is false
        if (shuffledQuestions.length === 0) return;

        // Pause cycling if input is not empty
        if (model.inputText && model.inputText.trim() !== '') return;

        const interval = setInterval(() => {
            // Double-check input is still empty before cycling
            if (model.inputText && model.inputText.trim() !== '') return;

            // Fade out
            setIsPlaceholderTransitioning(true);

            // After fade out completes, change placeholder and fade in
            setTimeout(() => {
                // Check again before updating placeholder
                if (model.inputText && model.inputText.trim() !== '') {
                    setIsPlaceholderTransitioning(false);
                    return;
                }

                setCurrentQuestionIndex(prevIndex => {
                    const nextIndex = (prevIndex + 1) % shuffledQuestions.length;

                    // If we've cycled through all, reshuffle
                    if (nextIndex === 0) {
                        const shuffled = [...shuffledQuestions];
                        for (let i = shuffled.length - 1; i > 0; i--) {
                            const j = Math.floor(Math.random() * (i + 1));
                            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                        }
                        setShuffledQuestions(shuffled);
                        setPlaceholder(shuffled[0]);
                        return 0;
                    } else {
                        setPlaceholder(shuffledQuestions[nextIndex]);
                        return nextIndex;
                    }
                });

                // Fade in after a brief moment to ensure placeholder has updated
                setTimeout(() => {
                    setIsPlaceholderTransitioning(false);
                }, 50);
            }, 300); // Wait for fade out to complete
        }, 20000);

        return () => clearInterval(interval);
    }, [suggestRequests, shuffledQuestions, model.inputText]);

    const handleSubmit = () => {
        const email = sendEmailResponse && userEmail ? userEmail : undefined;
        if (props.hasSelectedJob) {
            model.reply(agentMemory, email);
        } else {
            model.submit(agentMemory, email);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (isButtonEnabled) {
                handleSubmit();
            }
        } else if (e.key === 'Tab' && suggestRequests) {
            // Use placeholder as input value, but only if input is empty and exclude the "Cannot submit" message
            if (!model.inputText || model.inputText.trim() === '') {
                const currentPlaceholder = placeholderSourceJob?.status === "RUNNING"
                    ? "Cannot submit while agent is thinking..."
                    : placeholder;

                if (currentPlaceholder && currentPlaceholder !== "Cannot submit while agent is thinking...") {
                    e.preventDefault();
                    model.inputText = currentPlaceholder;
                }
            }
        } else if (suggestRequests && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && shuffledQuestions.length > 0) {
            const currentPlaceholder = placeholderSourceJob?.status === "RUNNING"
                ? "Cannot submit while agent is thinking..."
                : placeholder;

            if (currentPlaceholder && currentPlaceholder !== "Cannot submit while agent is thinking...") {
                const isInputEmpty = !model.inputText || model.inputText.trim() === '';
                const isInputMatchingPlaceholder = model.inputText.trim() === currentPlaceholder.trim();

                // Allow cycling if input is empty OR if input matches placeholder
                if (isInputEmpty || isInputMatchingPlaceholder) {
                    const textArea = textAreaRef.current?.resizableTextArea?.textArea;
                    if (textArea) {
                        const cursorPosition = textArea.selectionStart;
                        const textLength = textArea.value.length;

                        // Check if cursor is at top (position 0) for up arrow, or at bottom for down arrow
                        // If input is empty, always allow cycling
                        const isAtTop = cursorPosition === 0;
                        const isAtBottom = cursorPosition === textLength;

                        if (isInputEmpty || (e.key === 'ArrowUp' && isAtTop) || (e.key === 'ArrowDown' && isAtBottom)) {
                            e.preventDefault();

                            let newIndex: number;
                            if (e.key === 'ArrowUp') {
                                // Go to previous placeholder (wrap around)
                                newIndex = currentQuestionIndex === 0
                                    ? shuffledQuestions.length - 1
                                    : currentQuestionIndex - 1;
                            } else {
                                // Go to next placeholder (wrap around)
                                newIndex = (currentQuestionIndex + 1) % shuffledQuestions.length;
                            }

                            const newPlaceholder = shuffledQuestions[newIndex];
                            setCurrentQuestionIndex(newIndex);
                            setPlaceholder(newPlaceholder);

                            // Only update input text if it was matching the placeholder
                            if (isInputMatchingPlaceholder) {
                                model.inputText = newPlaceholder;
                            }

                            // Set cursor position: beginning for up arrow, end for down arrow
                            setTimeout(() => {
                                if (textArea) {
                                    const cursorPos = e.key === 'ArrowUp' ? 0 : textArea.value.length;
                                    textArea.setSelectionRange(cursorPos, cursorPos);
                                }
                            }, 0);
                        }
                    }
                }
            }
        }
    };

    const isButtonEnabled = (props.hasSelectedJob ? model.canReply : model.canSubmit) && !isAnimating;
    const isCentered = props.centered || false;

    return (
        <div style={{
            position: "relative",
            padding: isCentered ? "0" : "0.5em",
            zIndex: 1060
        }}>
            <style>{`
                #question-input::-webkit-scrollbar {
                    width: 8px;
                }
                #question-input::-webkit-scrollbar-track {
                    background: transparent;
                }
                #question-input::-webkit-scrollbar-thumb {
                    background: ${darkMode ? themeColors.borderColorSecondary : "#888"};
                    border-radius: 4px;
                    border: 2px solid transparent;
                    background-clip: padding-box;
                }
                #question-input::-webkit-scrollbar-thumb:hover {
                    background: ${themeColors.borderColor};
                    background-clip: padding-box;
                }
                #question-input::placeholder {
                    transition: opacity 0.3s ease-in-out;
                    color: ${themeColors.placeholderColor} !important;
                }
                #question-input.placeholder-fade-out::placeholder {
                    opacity: 0;
                }
                #question-input.placeholder-fade-in::placeholder {
                    opacity: 1;
                }
            `}</style>
            <ConditionalTooltip title={suggestRequests
                ? (shuffledQuestions.length > 0
                    ? "Enter to submit, Shift+Enter for new line, Tab to use placeholder, ↑/↓ to navigate placeholders"
                    : "Enter to submit, Shift+Enter for new line, Tab to use placeholder")
                : "Enter to submit, Shift+Enter for new line"}>
                <TextArea
                    ref={textAreaRef}
                    id="question-input"
                    className={isPlaceholderTransitioning ? "placeholder-fade-out" : "placeholder-fade-in"}
                    disabled={model.isSubmitting}
                    value={model.inputText}
                    onChange={e => {
                        model.inputText = e.target.value;
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholderSourceJob?.status === "RUNNING" ? "Cannot submit while agent is thinking..." : placeholder}
                    autoSize={{ minRows: 1, maxRows: 10 }}
                    style={{
                    width: "100%",
                    fontSize: isCentered ? "1.15em" : "1em",
                    lineHeight: isCentered ? "1.4em" : "1.3em",
                    border: `1px solid ${themeColors.borderColorSecondary}`,
                    borderRadius: isCentered ? "12px" : "8px",
                    padding: showEmailToggle
                        ? (isCentered ? "0.5em 5.3em 0.5em 1em" : "0.5em 5.4em 0.5em 0.5em")
                        : (isCentered ? "0.5em 2.7em 0.5em 1em" : "0.5em 2.8em 0.5em 0.5em"),
                    resize: "none",
                    backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                    color: darkMode ? "#ffffff" : "#000000"
                }}
                />
            </ConditionalTooltip>
            {allowEmail && showEmailToggle && (
                <ConditionalTooltip title="Send agent response to your email as well">
                    <button
                        onClick={() => setSendEmailResponse(!sendEmailResponse)}
                        disabled={model.isSubmitting || !userEmail}
                        style={{
                            position: "absolute",
                            bottom: isCentered ? "1.65em" : "2.1em",
                            right: isCentered ? "3.0em" : "3.6em",
                            width: isCentered ? "2.2em" : "2.2em",
                            height: isCentered ? "2.2em" : "2.2em",
                            borderRadius: "50%",
                            border: "none",
                            backgroundColor: sendEmailResponse
                                ? themeColors.primaryColor
                                : themeColors.borderColorSecondary,
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: (model.isSubmitting || !userEmail) ? "not-allowed" : "pointer",
                            transition: "background-color 0.2s",
                            fontSize: isCentered ? "1.15em" : "1em",
                            opacity: (!userEmail) ? 0.5 : 1
                        }}
                    >
                        <MailOutlined style={{ fontSize: isCentered ? "1em" : "0.9em" }} />
                    </button>
                </ConditionalTooltip>
            )}
            <ConditionalTooltip title={props.hasSelectedJob ? "Submit reply" : "Submit request"}>
                <button
                    onClick={handleSubmit}
                    disabled={!isButtonEnabled}
                    style={{
                        position: "absolute",
                        bottom: isCentered ? "1.65em" : "2.1em",
                        right: isCentered ? "0.5em" : "1.1em",
                        width: isCentered ? "2.2em" : "2.2em",
                        height: isCentered ? "2.2em" : "2.2em",
                        borderRadius: "50%",
                        border: "none",
                        backgroundColor: isButtonEnabled
                            ? themeColors.primaryColor
                            : themeColors.borderColorSecondary,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: isButtonEnabled ? "pointer" : "not-allowed",
                        transition: "background-color 0.2s",
                        fontSize: isCentered ? "1.15em" : "1em"
                    }}
                >
                    <ArrowUpOutlined style={{ fontSize: isCentered ? "1em" : "0.9em" }} />
                </button>
            </ConditionalTooltip>
            <div style={{
                marginTop: "0.5em",
                fontSize: isCentered ? "0.85em" : "0.75em",
                color: darkMode ? "#888888" : "#AFAFAF",
                textAlign: "center"
            }}>
                AI can make mistakes. Submit feedback to help us improve.
            </div>
        </div>
    )
}